import { Document } from '@contentful/rich-text-types'
import { TopLevelBlock } from '@contentful/rich-text-types'
import type { Asset, AssetFile, Entry, EntrySkeletonType } from 'contentful'

export type ContentfulImageOptions = {
  /** 1 - 100 */
  quality?: number
  width?: number
  height?: number
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
  radius?: number
  focus?:
    | 'center'
    | 'top'
    | 'right'
    | 'left'
    | 'bottom'
    | 'top_right'
    | 'top_left'
    | 'bottom_right'
    | 'bottom_left'
    | 'face'
    | 'faces'
}

/**
 * Create optimized image urls for Contentful assets
 * @param asset
 * @param options
 * @returns
 *
 */

type CreateOptomizedContentfulImageProps = {
  file?: AssetFile
}

export const createOptimizedContentfulImage = (
  asset: CreateOptomizedContentfulImageProps,
  options?: ContentfulImageOptions & { expectedType?: 'webp' | 'avif' },
): string => {
  const { quality, expectedType } = options || { quality: 80, expectedType: 'webp' }

  const url = asset.file?.url
  const assetFileType = asset.file?.contentType
  if (assetFileType == 'image/svg+xml') return `${url}?q=${quality}`

  if (expectedType) return `${url}?fm=${expectedType}&q=${quality}`
  return `${url}?q=${quality}`
}

export const createOptimizedContentfulImageStr = (
  url: string,
  options?: ContentfulImageOptions & { expectedType?: 'webp' | 'avif' },
) => {
  const { quality, expectedType } = options || { quality: 80, expectedType: 'webp' }

  if (expectedType) return `${url}?fm=${expectedType}&q=${quality}`
  return `${url}?q=${quality}`
}

/**
 * TODO: make this function more generic on the condition itself left side
 * @param content
 * @param condition
 * @returns
 */

export const findItemInPageContent = (
  content: Array<TopLevelBlock>,
  condition: string,
): Document => {
  return content?.find(
    (item: TopLevelBlock) => item?.data?.target?.fields?.blockId === condition,
  ) as unknown as Document
}

export const filterItemsInPageContent = (content: Array<TopLevelBlock>, condition: string) => {
  return content?.filter((item: TopLevelBlock) => item?.data?.target?.fields?.blockId === condition)
}

// Find a way to understand which one to use or create my own types in here.
export const deconstructNode = <T extends EntrySkeletonType>(node: {
  data?: { target?: unknown }
  fields?: unknown
}) => {
  if (node?.data?.target) {
    const { fields, metadata, sys } = node.data.target as Entry<
      T,
      'WITHOUT_UNRESOLVABLE_LINKS',
      string
    >
    return { fields: fields, metadata: metadata, sys: sys }
  }
  if (node?.fields) {
    const { fields, metadata, sys } = node as Entry<T, 'WITHOUT_UNRESOLVABLE_LINKS', string>
    return { fields: fields, metadata: metadata, sys: sys }
  }
  return { fields: undefined, metadata: undefined, sys: undefined }
}

export const getContentfulAsset = (
  asset?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>,
): Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined => {
  if (!asset) return undefined
  if (asset.sys.type === 'Asset') return asset as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>

  return undefined
}
